import { Autocomplete, TextField, createFilterOptions } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { TimeZoneOption, getTimeZoneOptions, toTimeZoneOption } from '@utils/timeZones';

// ----------------------------------------------------------------------

export type RHFTimeZoneSelectProps = {
  name: string;
  label?: string;
  required?: boolean;
  helperText?: ReactNode;
};

// "America/Denver" and "Denver" should both find the zone; underscores are typed as spaces.
const filterOptions = createFilterOptions<TimeZoneOption>({
  stringify: ({ id }) => `${id} ${id.replace(/_/g, ' ')}`,
});

// A stored id may be absent from the browser's list (e.g. a legacy link name). Still show it.
const resolveValue = (id: string, options: TimeZoneOption[]) => {
  if (!id) return { value: null, allOptions: options };

  const known = options.find((option) => option.id === id);
  if (known) return { value: known, allOptions: options };

  const fallback = toTimeZoneOption(id);
  return { value: fallback, allOptions: [fallback, ...options] };
};

export function RHFTimeZoneSelect({ name, label = 'Time Zone', required, helperText }: RHFTimeZoneSelectProps) {
  const { control, setValue } = useFormContext();

  // Labels carry the current time in each zone, so rebuild them on every mount.
  const options = useMemo(() => getTimeZoneOptions(), []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const { value, allOptions } = resolveValue(field.value, options);

        return (
          <Autocomplete
            id={`rhf-time-zone-select-${name}`}
            value={value}
            fullWidth
            autoHighlight
            onBlur={field.onBlur}
            onChange={(_, nextValue: TimeZoneOption | null) =>
              setValue(name, nextValue?.id ?? '', { shouldValidate: true })
            }
            options={allOptions}
            filterOptions={filterOptions}
            isOptionEqualToValue={(option, { id }) => option.id === id}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                required={required}
                error={!!error}
                helperText={error?.message ?? helperText}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    autoComplete: 'new-password',
                  },
                }}
              />
            )}
          />
        );
      }}
    />
  );
}
